import React, { useState, useEffect, useCallback } from "react";
import { Trash2, Eye, X, Phone, Mail, Calendar, User } from "lucide-react";
import Swal from "sweetalert2";
import Loader from "../../components/common/Loader";

const STATUS_OPTIONS = ["pending", "confirmed", "completed", "cancelled"];

const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const DemoBooking = () => {
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchRegistrations = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (statusFilter) params.append("status", statusFilter);

      const response = await fetch(
        `${API_URL}/demoClass/registrations?${params.toString()}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data?.success) {
        setRegistrations(data.data || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      } else {
        throw new Error(data?.message || "Failed to fetch registrations");
      }
    } catch (err) {
      console.error("Error fetching registrations:", err);
      setError(err.message);
      Swal.fire({
        title: "Error!",
        text: "Failed to load demo bookings. Please try again.",
        icon: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [API_URL, page, limit, statusFilter]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleViewDetails = (registration) => {
    setSelectedRegistration(registration);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedRegistration(null);
  };

  const handleStatusChange = async (registrationId, newStatus) => {
    try {
      setUpdatingId(registrationId);
      const response = await fetch(
        `${API_URL}/demoClass/registrations/${registrationId}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setRegistrations((prev) =>
          prev.map((reg) =>
            reg._id === registrationId ? { ...reg, status: newStatus } : reg,
          ),
        );

        if (selectedRegistration?._id === registrationId) {
          setSelectedRegistration((prev) => ({ ...prev, status: newStatus }));
        }

        Swal.fire({
          title: "Updated!",
          text: "Registration status updated successfully.",
          icon: "success",
          timer: 1200,
          showConfirmButton: false,
        });
      } else {
        throw new Error(data.message || "Failed to update status");
      }
    } catch (err) {
      console.error("Status update error:", err);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while updating the status.",
        icon: "error",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (registrationId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This booking will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) {
      Swal.fire({
        title: "Cancelled",
        text: "The booking is safe 😊",
        icon: "info",
        timer: 1200,
        showConfirmButton: false,
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/demoClass/registrations/${registrationId}`,
        { method: "DELETE" },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        Swal.fire({
          title: "Deleted!",
          text: "The booking has been deleted successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        if (selectedRegistration?._id === registrationId) {
          handleCloseDetailsModal();
        }
        fetchRegistrations();
      } else {
        throw new Error(data.message || "Failed to delete booking");
      }
    } catch (err) {
      console.error("Delete error:", err);
      Swal.fire({
        title: "Error!",
        text: "Something went wrong while deleting the booking.",
        icon: "error",
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCourseName = (course) => {
    if (!course) return "N/A";
    if (typeof course === "object") return course.name || course.title || "N/A";
    return course;
  };

  return (
    <div className="min-h-screen p-6 dashboardContainer">
      <div className="w-full mx-auto">
        <div className="mb-4 mt-6">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-primaryTextColor mb-2">
                Demo Bookings
              </h1>
              <p className="text-secondaryTextColor text-sm">
                {total} total {total === 1 ? "registration" : "registrations"}
              </p>
            </div>

            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primaryColor focus:border-transparent bg-white"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <div className="col-span-full text-center py-16">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
              Error Loading Bookings
            </h3>
            <p className="text-secondaryTextColor">{error}</p>
            <button
              onClick={fetchRegistrations}
              className="mt-4 bg-primaryBtnColor text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-all"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {registrations.map((registration) => (
                <div
                  key={registration._id}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 group relative overflow-hidden p-5"
                >
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-primaryTextColor line-clamp-1">
                      {registration.studentName}
                    </h3>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${
                        STATUS_STYLES[registration.status] ||
                        "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {registration.status}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-sm text-secondaryTextColor mb-4">
                    <p className="flex items-center gap-2">
                      <Mail size={14} className="flex-shrink-0" />
                      <span className="truncate">{registration.email}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone size={14} className="flex-shrink-0" />
                      {registration.phone}
                    </p>
                    <p className="flex items-center gap-2">
                      <Calendar size={14} className="flex-shrink-0" />
                      Demo on {formatDate(registration.demoStartDate)}
                    </p>
                    <p className="text-primaryTextColor font-medium">
                      Course: {getCourseName(registration.course)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <select
                      value={registration.status}
                      onChange={(e) =>
                        handleStatusChange(registration._id, e.target.value)
                      }
                      disabled={updatingId === registration._id}
                      className="flex-1 text-sm px-2 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-primaryColor focus:border-transparent disabled:opacity-50"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={() => handleViewDetails(registration)}
                      className="bg-blue-500 text-white p-2 rounded hover:bg-blue-600 transition-all"
                      title="View details"
                    >
                      <Eye size={16} />
                    </button>

                    <button
                      onClick={() => handleDelete(registration._id)}
                      className="bg-red-500 text-white p-2 rounded hover:bg-red-600 transition-all"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="h-1 bg-gradient-to-r from-primaryColor to-secondaryColor transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 absolute bottom-0 left-0 right-0"></div>
                </div>
              ))}
            </div>

            {registrations.length === 0 && (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">📋</div>
                <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
                  No Bookings Found
                </h3>
                <p className="text-secondaryTextColor">
                  {statusFilter
                    ? `No registrations with status "${statusFilter}".`
                    : "No demo class registrations yet."}
                </p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-primaryTextColor hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-secondaryTextColor px-2">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-primaryTextColor hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isDetailsModalOpen && selectedRegistration && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-primaryTextColor flex items-center gap-2">
                <User size={22} />
                Booking Details
              </h2>
              <button
                onClick={handleCloseDetailsModal}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)] space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-secondaryTextColor">
                  Status
                </span>
                <span
                  className={`text-xs font-semibold px-3 py-1 rounded-full ${
                    STATUS_STYLES[selectedRegistration.status] ||
                    "bg-gray-100 text-gray-800"
                  }`}
                >
                  {selectedRegistration.status}
                </span>
              </div>

              <DetailRow
                label="Student Name"
                value={selectedRegistration.studentName}
              />
              <DetailRow label="Email" value={selectedRegistration.email} />
              <DetailRow label="Phone" value={selectedRegistration.phone} />
              <DetailRow
                label="Course"
                value={getCourseName(selectedRegistration.course)}
              />
              <DetailRow
                label="Guardian Name"
                value={selectedRegistration.guardianName || "—"}
              />
              <DetailRow
                label="Demo Start Date"
                value={formatDate(selectedRegistration.demoStartDate)}
              />
              <DetailRow
                label="Registered On"
                value={formatDate(selectedRegistration.createdAt)}
              />
              {selectedRegistration.message && (
                <div>
                  <p className="text-sm font-semibold text-secondaryTextColor mb-1">
                    Message
                  </p>
                  <p className="text-primaryTextColor bg-gray-50 rounded-lg p-3 text-sm">
                    {selectedRegistration.message}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-3 p-6 border-t">
              <select
                value={selectedRegistration.status}
                onChange={(e) =>
                  handleStatusChange(selectedRegistration._id, e.target.value)
                }
                disabled={updatingId === selectedRegistration._id}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primaryColor focus:border-transparent disabled:opacity-50"
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleDelete(selectedRegistration._id)}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-all flex items-center gap-2"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex justify-between items-start gap-4">
    <span className="text-sm font-semibold text-secondaryTextColor whitespace-nowrap">
      {label}
    </span>
    <span className="text-primaryTextColor text-sm text-right">{value}</span>
  </div>
);

export default DemoBooking;
